import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { useUserAuth } from "../../../context/userAuthContext";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart,
  Legend,
} from "recharts";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Shield,
  CreditCard,
  MessageCircle,
  Plus,
  Minus,
  Eye,
  EyeOff,
  ArrowUpRight,
  ArrowDownRight,
  MoreHorizontal,
  CheckCircle,
  Clock,
  AlertCircle,
  Receipt,
  Activity,
  Wallet,
  Target,
  PiggyBank,
  Bell,
  Settings,
  User,
  ChevronRight,
  Calendar,
  Filter,
  Download,
  RefreshCw,
  Zap,
  Sparkles,
  BarChart3,
  AlertTriangle,
  Info,
  ArrowLeft,
  ArrowRight,
  Sparkle,
  Lightbulb,
} from "lucide-react";

const HomeDashboard = () => {
  const { user } = useUserAuth();
  const navigate = useNavigate();
  const [balanceVisible, setBalanceVisible] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingExpenses, setLoadingExpenses] = useState(true);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);
  const [loadingIncomes, setLoadingIncomes] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState("mes"); // dia, semana, mes, año

  const menuItems = [
    {
      title: "Pólizas",
      icon: Shield,
      path: "/polizas",
      color: "from-[#218e01] to-[#5fc500]",
    },
    {
      title: "Cashback",
      icon: CreditCard,
      path: "/cashback",
      badge: "PRO",
      color: "from-[#5fc500] to-[#11653a]",
    },
    {
      title: "Consultas",
      icon: MessageCircle,
      path: "/consultas",
      color: "from-[#11653a] to-[#096365]",
    },
    {
      title: "Inversiones",
      icon: TrendingUp,
      path: "/inversiones",
      color: "from-[#096365] to-[#5ba49e]",
    },
    {
      title: "Ingresos",
      icon: Plus,
      path: "/incomes",
      color: "from-[#5ba49e] to-[#218e01]",
    },
    {
      title: "Gastos",
      icon: Minus,
      path: "/expenses",
      color: "from-[#218e01] to-[#5fc500]",
    },
  ];

  useEffect(() => {
    if (user) {
      fetchUserData();
      loadSubscriptions();
      loadExpenses();
      loadIncomes();
    }
  }, [user, selectedPeriod]);

  const fetchUserData = async () => {
    try {
      if (user?.uid) {
        const docRef = doc(db, "Users", user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const userData = docSnap.data();
          setUserInfo({
            name: `${userData.nombre || ""} ${userData.apellido || ""}`.trim(),
            nickname: userData.nickname || "",
            email: userData.email || user.email,
          });
        } else {
          setUserInfo({
            name: user.displayName || "Usuario",
            nickname: "",
            email: user.email,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptions = async () => {
    if (!user) return;
    setLoadingSubscriptions(true);
    try {
      const subscriptionsRef = collection(db, "subscriptions", user.uid, "userSubscriptions");
      const q = query(subscriptionsRef, orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      const subscriptionsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setSubscriptions(subscriptionsData);
    } catch (error) {
      console.error("Error al cargar suscripciones:", error);
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  const loadExpenses = async () => {
    if (!user) return;
    setLoadingExpenses(true);
    try {
      const expensesRef = collection(db, "expenses", user.uid, "userExpenses");
      const q = query(expensesRef, orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      const expensesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setExpenses(expensesData);
    } catch (error) {
      console.error("Error al cargar gastos:", error);
    } finally {
      setLoadingExpenses(false);
    }
  };

  const loadIncomes = async () => {
    if (!user) return;
    setLoadingIncomes(true);
    try {
      const incomesRef = collection(db, "incomes", user.uid, "userIncomes");
      const q = query(incomesRef, orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      const incomesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setIncomes(incomesData);
    } catch (error) {
      console.error("Error al cargar ingresos:", error);
    } finally {
      setLoadingIncomes(false);
    }
  };

  // Función para obtener el rango de fechas según el período seleccionado
  const getDateRange = (period) => {
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    switch (period) {
      case "dia":
        return {
          start: startOfDay,
          end: new Date(startOfDay.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "semana":
        const startOfWeek = new Date(startOfDay);
        startOfWeek.setDate(today.getDate() - today.getDay());
        return {
          start: startOfWeek,
          end: new Date(startOfWeek.getTime() + 7 * 24 * 60 * 60 * 1000 - 1)
        };
      case "mes":
        return {
          start: new Date(today.getFullYear(), today.getMonth(), 1),
          end: new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59)
        };
      case "año":
        return {
          start: new Date(today.getFullYear(), 0, 1),
          end: new Date(today.getFullYear(), 11, 31, 23, 59, 59)
        };
      default:
        return {
          start: new Date(today.getFullYear(), today.getMonth(), 1),
          end: new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59)
        };
    }
  };

  // Calcular métricas reales basadas en los datos
  const getTotalMonthlySubscriptions = () => {
    return subscriptions
      .filter(sub => sub.activa)
      .reduce((total, sub) => {
        let monthlyAmount = sub.cantidad;
        switch (sub.frecuenciaPago) {
          case "semanal":
            monthlyAmount = sub.cantidad * 4.33;
            break;
          case "trimestral":
            monthlyAmount = sub.cantidad / 3;
            break;
          case "semestral":
            monthlyAmount = sub.cantidad / 6;
            break;
          case "anual":
            monthlyAmount = sub.cantidad / 12;
            break;
        }
        return total + monthlyAmount;
      }, 0);
  };

  const getTotalPeriodExpenses = () => {
    const dateRange = getDateRange(selectedPeriod);
    return expenses
      .filter(expense => {
        const expenseDate = new Date(expense.fecha);
        return expenseDate >= dateRange.start && expenseDate <= dateRange.end;
      })
      .reduce((total, expense) => total + expense.cantidad, 0);
  };

  const getTotalPeriodIncomes = () => {
    const dateRange = getDateRange(selectedPeriod);
    return incomes
      .filter(income => {
        const incomeDate = new Date(income.fecha);
        return incomeDate >= dateRange.start && incomeDate <= dateRange.end;
      })
      .reduce((total, income) => total + income.cantidad, 0);
  };

  // Calcular balance real
  const getCurrentBalance = () => {
    const totalIncomes = getTotalPeriodIncomes();
    const totalExpenses = getTotalPeriodExpenses();
    const totalSubscriptions = getTotalMonthlySubscriptions();
    return totalIncomes - totalExpenses - totalSubscriptions;
  };

  // Obtener datos históricos para gráficos (últimos 6 meses)
  const getHistoricalData = () => {
    const months = [];
    const today = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);
      
      const monthIncomes = incomes
        .filter(income => {
          const incomeDate = new Date(income.fecha);
          return incomeDate >= monthStart && incomeDate <= monthEnd;
        })
        .reduce((sum, income) => sum + income.cantidad, 0);
      
      const monthExpenses = expenses
        .filter(expense => {
          const expenseDate = new Date(expense.fecha);
          return expenseDate >= monthStart && expenseDate <= monthEnd;
        })
        .reduce((sum, expense) => sum + expense.cantidad, 0);
      
      months.push({
        month: date.toLocaleDateString("es-MX", { month: "short" }),
        income: monthIncomes,
        outcome: monthExpenses,
        balance: monthIncomes - monthExpenses,
      });
    }
    
    return months;
  };

  // Obtener gastos por categoría
  const getExpensesByCategory = () => {
    const dateRange = getDateRange(selectedPeriod);
    const categoryTotals = {};
    
    expenses
      .filter(expense => {
        const expenseDate = new Date(expense.fecha);
        return expenseDate >= dateRange.start && expenseDate <= dateRange.end;
      })
      .forEach(expense => {
        if (categoryTotals[expense.categoria]) {
          categoryTotals[expense.categoria].value += expense.cantidad;
        } else {
          categoryTotals[expense.categoria] = {
            name: expense.categoria,
            value: expense.cantidad,
            color: getCategoryColor(expense.categoria),
          };
        }
      });
    
    return Object.values(categoryTotals).sort((a, b) => b.value - a.value).slice(0, 6);
  };

  const getCategoryColor = (category) => {
    const colors = [
      "#218e01", "#5fc500", "#11653a", "#096365", "#5ba49e",
      "#3B82F6", "#EF4444", "#10B981", "#F59E0B", "#8B5CF6"
    ];
    const index = category.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
  };

  // Obtener ingresos por categoría
  const getIncomesByCategory = () => {
    const dateRange = getDateRange(selectedPeriod);
    const categoryTotals = {};
    
    incomes
      .filter(income => {
        const incomeDate = new Date(income.fecha);
        return incomeDate >= dateRange.start && incomeDate <= dateRange.end;
      })
      .forEach(income => {
        if (categoryTotals[income.categoria]) {
          categoryTotals[income.categoria].value += income.cantidad;
        } else {
          categoryTotals[income.categoria] = {
            name: income.categoria,
            value: income.cantidad,
            color: "#10B981",
          };
        }
      });
    
    return Object.values(categoryTotals).sort((a, b) => b.value - a.value).slice(0, 5);
  };

  // Calcular comparación con período anterior
  const getPeriodComparison = () => {
    const currentRange = getDateRange(selectedPeriod);
    const previousRange = getPreviousPeriodRange(selectedPeriod);
    
    const currentIncomes = incomes
      .filter(income => {
        const incomeDate = new Date(income.fecha);
        return incomeDate >= currentRange.start && incomeDate <= currentRange.end;
      })
      .reduce((sum, income) => sum + income.cantidad, 0);
    
    const previousIncomes = incomes
      .filter(income => {
        const incomeDate = new Date(income.fecha);
        return incomeDate >= previousRange.start && incomeDate <= previousRange.end;
      })
      .reduce((sum, income) => sum + income.cantidad, 0);
    
    const currentExpenses = expenses
      .filter(expense => {
        const expenseDate = new Date(expense.fecha);
        return expenseDate >= currentRange.start && expenseDate <= currentRange.end;
      })
      .reduce((sum, expense) => sum + expense.cantidad, 0);
    
    const previousExpenses = expenses
      .filter(expense => {
        const expenseDate = new Date(expense.fecha);
        return expenseDate >= previousRange.start && expenseDate <= previousRange.end;
      })
      .reduce((sum, expense) => sum + expense.cantidad, 0);
    
    const incomeChange = previousIncomes > 0 
      ? ((currentIncomes - previousIncomes) / previousIncomes * 100).toFixed(1)
      : currentIncomes > 0 ? "100" : "0";
    
    const expenseChange = previousExpenses > 0
      ? ((currentExpenses - previousExpenses) / previousExpenses * 100).toFixed(1)
      : currentExpenses > 0 ? "100" : "0";
    
    return {
      incomeChange: parseFloat(incomeChange),
      expenseChange: parseFloat(expenseChange),
      currentIncomes,
      previousIncomes,
      currentExpenses,
      previousExpenses,
    };
  };

  const getPreviousPeriodRange = (period) => {
    const today = new Date();
    
    switch (period) {
      case "dia":
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return {
          start: new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate()),
          end: new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59)
        };
      case "semana":
        const lastWeek = new Date(today);
        lastWeek.setDate(lastWeek.getDate() - 7);
        const startOfLastWeek = new Date(lastWeek);
        startOfLastWeek.setDate(lastWeek.getDate() - lastWeek.getDay());
        return {
          start: startOfLastWeek,
          end: new Date(startOfLastWeek.getTime() + 7 * 24 * 60 * 60 * 1000 - 1)
        };
      case "mes":
        return {
          start: new Date(today.getFullYear(), today.getMonth() - 1, 1),
          end: new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59)
        };
      case "año":
        return {
          start: new Date(today.getFullYear() - 1, 0, 1),
          end: new Date(today.getFullYear() - 1, 11, 31, 23, 59, 59)
        };
      default:
        return {
          start: new Date(today.getFullYear(), today.getMonth() - 1, 1),
          end: new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59)
        };
    }
  };

  // Obtener suscripciones próximas a vencer
  const getUpcomingSubscriptions = () => {
    const today = new Date();
    return subscriptions
      .filter(sub => sub.activa)
      .filter(sub => {
        const paymentDate = new Date(sub.fechaProximoPago);
        const diffTime = paymentDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays >= 0 && diffDays <= 7;
      })
      .sort((a, b) => new Date(a.fechaProximoPago) - new Date(b.fechaProximoPago))
      .slice(0, 5);
  };

  // Obtener insights financieros
  const getFinancialInsights = () => {
    const insights = [];
    const totalIncomes = getTotalPeriodIncomes();
    const totalExpenses = getTotalPeriodExpenses();
    const totalSubscriptions = getTotalMonthlySubscriptions();
    const balance = totalIncomes - totalExpenses - totalSubscriptions;
    const comparison = getPeriodComparison();

    // Insight 1: Balance
    if (balance < 0) {
      insights.push({
        type: "warning",
        icon: AlertTriangle,
        title: "Balance Negativo",
        message: `Tus gastos superan tus ingresos en $${Math.abs(balance).toLocaleString("es-MX", { minimumFractionDigits: 2 })} este período.`,
        action: "Revisar gastos",
        actionPath: "/expenses",
      });
    } else if (balance > 0) {
      insights.push({
        type: "success",
        icon: Sparkles,
        title: "¡Excelente!",
        message: `Tienes un balance positivo de $${balance.toLocaleString("es-MX", { minimumFractionDigits: 2 })} este período.`,
        action: "Ver detalles",
        actionPath: "/dashboard",
      });
    }

    // Insight 2: Comparación de ingresos
    if (comparison.incomeChange > 10) {
      insights.push({
        type: "success",
        icon: TrendingUp,
        title: "Ingresos en Aumento",
        message: `Tus ingresos aumentaron ${comparison.incomeChange}% comparado con el período anterior.`,
        action: "Ver ingresos",
        actionPath: "/incomes",
      });
    } else if (comparison.incomeChange < -10) {
      insights.push({
        type: "warning",
        icon: TrendingDown,
        title: "Ingresos en Descenso",
        message: `Tus ingresos disminuyeron ${Math.abs(comparison.incomeChange)}% comparado con el período anterior.`,
        action: "Revisar ingresos",
        actionPath: "/incomes",
      });
    }

    // Insight 3: Suscripciones
    if (totalSubscriptions > totalIncomes * 0.3) {
      insights.push({
        type: "warning",
        icon: CreditCard,
        title: "Suscripciones Elevadas",
        message: `Tus suscripciones representan más del 30% de tus ingresos. Considera revisarlas.`,
        action: "Gestionar suscripciones",
        actionPath: "/subscriptions",
      });
    }

    // Insight 4: Próximos pagos
    const upcoming = getUpcomingSubscriptions();
    if (upcoming.length > 0) {
      insights.push({
        type: "info",
        icon: Bell,
        title: "Pagos Próximos",
        message: `Tienes ${upcoming.length} suscripción${upcoming.length > 1 ? "es" : ""} con pago próximo esta semana.`,
        action: "Ver suscripciones",
        actionPath: "/subscriptions",
      });
    }

    return insights.slice(0, 3);
  };

  const historicalData = getHistoricalData();
  const expensesByCategory = getExpensesByCategory();
  const incomesByCategory = getIncomesByCategory();
  const totalPeriodExpenses = getTotalPeriodExpenses();
  const totalPeriodIncomes = getTotalPeriodIncomes();
  const totalMonthlySubscriptions = getTotalMonthlySubscriptions();
  const currentBalance = getCurrentBalance();
  const comparison = getPeriodComparison();
  const upcomingSubscriptions = getUpcomingSubscriptions();
  const financialInsights = getFinancialInsights();

  const mainStats = [
    {
      title: "Balance Actual",
      value: `$${currentBalance.toLocaleString("es-MX", { minimumFractionDigits: 2 })}`,
      icon: Wallet,
      gradient: "from-[#218e01] to-[#5fc500]",
      change: comparison.incomeChange - comparison.expenseChange >= 0 ? "+" : "",
      changeValue: `${(comparison.incomeChange - comparison.expenseChange).toFixed(1)}%`,
      isPositive: currentBalance >= 0,
      description: selectedPeriod === "mes" ? "este mes" : `este ${selectedPeriod}`,
    },
    {
      title: "Ingresos",
      value: `$${totalPeriodIncomes.toLocaleString("es-MX", { minimumFractionDigits: 2 })}`,
      icon: TrendingUp,
      gradient: "from-[#5fc500] to-[#11653a]",
      change: comparison.incomeChange >= 0 ? "+" : "",
      changeValue: `${comparison.incomeChange}%`,
      isPositive: comparison.incomeChange >= 0,
      description: `vs. período anterior`,
    },
    {
      title: "Gastos",
      value: `$${totalPeriodExpenses.toLocaleString("es-MX", { minimumFractionDigits: 2 })}`,
      icon: TrendingDown,
      gradient: "from-[#096365] to-[#5ba49e]",
      change: comparison.expenseChange >= 0 ? "+" : "",
      changeValue: `${comparison.expenseChange}%`,
      isPositive: false,
      description: `vs. período anterior`,
    },
    {
      title: "Suscripciones",
      value: `$${totalMonthlySubscriptions.toLocaleString("es-MX", { minimumFractionDigits: 2 })}`,
      icon: RefreshCw,
      gradient: "from-[#5ba49e] to-[#218e01]",
      change: "",
      changeValue: `${subscriptions.filter(s => s.activa).length} activas`,
      isPositive: true,
      description: "mensual",
    },
  ];

  const recentTransactions = [
    ...expenses.slice(0, 3).map(expense => ({
      id: expense.id,
      type: "expense",
      title: expense.descripcion,
      category: expense.categoria,
      amount: `-$${expense.cantidad.toLocaleString("es-MX", { minimumFractionDigits: 2 })}`,
      date: new Date(expense.fecha).toLocaleDateString("es-MX"),
      status: "completed",
      icon: Minus,
      color: "text-red-600",
      bgColor: "bg-red-50",
    })),
    ...incomes.slice(0, 2).map(income => ({
      id: income.id,
      type: "income",
      title: income.descripcion,
      category: income.categoria,
      amount: `+$${income.cantidad.toLocaleString("es-MX", { minimumFractionDigits: 2 })}`,
      date: new Date(income.fecha).toLocaleDateString("es-MX"),
      status: "completed",
      icon: Plus,
      color: "text-green-600",
      bgColor: "bg-green-50",
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-white">
        <div className="text-center bg-gray-50 rounded-2xl shadow-xl p-8 max-w-md w-full border border-gray-200">
          <AlertCircle className="w-16 h-16 text-[#096365] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Acceso no autorizado</h2>
          <p className="text-[#096365]">Por favor inicia sesión para continuar</p>
        </div>
      </div>
    );
  }

  // Estado de carga general
  const isLoading = loading || loadingExpenses || loadingSubscriptions || loadingIncomes;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 pb-20 sm:pb-8">
      {/* Header Mejorado - Super Responsivo */}
      <div className="bg-white/95 backdrop-blur-lg border-b border-slate-200/50 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-4 lg:py-6">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-[#218e01] to-[#5fc500] bg-clip-text text-transparent truncate">
                  {userInfo?.name ? `Hola, ${userInfo.name.split(' ')[0]}` : 'Bienvenido'}
                </h1>
                <p className="text-xs sm:text-sm text-slate-600 mt-1">
                  {new Date().toLocaleDateString("es-MX", { 
                    weekday: "long", 
                    year: "numeric", 
                    month: "long", 
                    day: "numeric" 
                  })}
                </p>
              </div>
              <button
                onClick={() => {
                  loadSubscriptions();
                  loadExpenses();
                  loadIncomes();
                }}
                className="p-2 sm:p-2.5 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0 ml-2"
                title="Actualizar datos"
                disabled={isLoading}
              >
                <RefreshCw className={`w-4 h-4 sm:w-5 sm:h-5 text-slate-600 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-xs sm:text-sm bg-white shadow-sm"
              >
                <option value="dia">Hoy</option>
                <option value="semana">Esta Semana</option>
                <option value="mes">Este Mes</option>
                <option value="año">Este Año</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && expenses.length === 0 && incomes.length === 0 && subscriptions.length === 0 ? (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#5fc500] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600 font-medium">Cargando tus finanzas...</p>
          </div>
        </div>
      ) : (
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Main Stats Cards - Super Responsivas */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6 lg:mb-8">
          {mainStats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="group bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-slate-200/50 relative overflow-hidden cursor-pointer"
                onClick={index === 2 ? () => navigate("/expenses") : index === 1 ? () => navigate("/incomes") : index === 3 ? () => navigate("/subscriptions") : undefined}
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${stat.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
                <div className="relative">
                  <div className="flex items-center justify-between mb-2 sm:mb-3 lg:mb-4">
                    <div className={`p-2 sm:p-3 lg:p-4 bg-gradient-to-r ${stat.gradient} rounded-lg sm:rounded-xl shadow-md group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="w-4 h-4 sm:w-5 sm:h-6 lg:w-7 text-white" />
                    </div>
                    {index === 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setBalanceVisible(!balanceVisible);
                        }}
                        className="p-1.5 sm:p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      >
                        {balanceVisible ? (
                          <Eye className="w-3 h-3 sm:w-4 sm:h-5 text-slate-600" />
                        ) : (
                          <EyeOff className="w-3 h-3 sm:w-4 sm:h-5 text-slate-600" />
                        )}
                      </button>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] sm:text-xs lg:text-sm font-medium text-slate-600 mb-1 sm:mb-2 truncate">
                      {stat.title}
                    </p>
                    <p className="text-base sm:text-xl lg:text-2xl xl:text-3xl font-bold text-slate-900 mb-1 sm:mb-2 truncate">
                      {balanceVisible || index !== 0 ? stat.value : "••••••"}
                    </p>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        {stat.change && (
                          <>
                            {stat.isPositive ? (
                              <ArrowUpRight className="w-3 h-3 sm:w-4 h-4 text-[#5fc500]" />
                            ) : (
                              <ArrowDownRight className="w-3 h-3 sm:w-4 h-4 text-red-500" />
                            )}
                            <span
                              className={`text-[10px] sm:text-xs lg:text-sm font-semibold ${stat.isPositive ? "text-[#5fc500]" : "text-red-500"
                                }`}
                            >
                              {stat.change}{stat.changeValue}
                            </span>
                          </>
                        )}
                        {!stat.change && (
                          <span className="text-[10px] sm:text-xs text-slate-500 truncate">
                            {stat.changeValue}
                          </span>
                        )}
                      </div>
                      <span className="text-[9px] sm:text-xs text-slate-500 truncate">
                        {stat.description}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Financial Insights */}
        {financialInsights.length > 0 && (
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Lightbulb className="w-5 h-5 text-[#5fc500]" />
              <h2 className="text-lg sm:text-xl font-bold text-slate-900">Insights Financieros</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {financialInsights.map((insight, index) => {
                const Icon = insight.icon;
                const bgColors = {
                  success: "bg-green-50 border-green-200",
                  warning: "bg-orange-50 border-orange-200",
                  info: "bg-blue-50 border-blue-200",
                };
                const textColors = {
                  success: "text-green-800",
                  warning: "text-orange-800",
                  info: "text-blue-800",
                };
                const iconColors = {
                  success: "text-green-600",
                  warning: "text-orange-600",
                  info: "text-blue-600",
                };
                return (
                  <div
                    key={index}
                    className={`${bgColors[insight.type]} border rounded-xl p-4 hover:shadow-md transition-shadow cursor-pointer`}
                    onClick={() => navigate(insight.actionPath)}
                  >
                    <div className="flex items-start gap-3">
                      <Icon className={`w-5 h-5 ${iconColors[insight.type]} flex-shrink-0 mt-0.5`} />
                      <div className="flex-1">
                        <h3 className={`font-semibold ${textColors[insight.type]} mb-1`}>
                          {insight.title}
                        </h3>
                        <p className={`text-sm ${textColors[insight.type]} opacity-90 mb-2`}>
                          {insight.message}
                        </p>
                        <span className={`text-xs font-medium ${textColors[insight.type]} underline`}>
                          {insight.action} →
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Charts Section - Mejorada */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Income vs Expenses Chart */}
          <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-lg border border-slate-200/50">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900">Ingresos vs Gastos</h3>
                <p className="text-sm text-slate-600">Últimos 6 meses</p>
              </div>
              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-2 text-[#5fc500]">
                  <div className="w-3 h-3 bg-[#5fc500] rounded-full"></div>
                  <span className="text-xs sm:text-sm font-medium">Ingresos</span>
                </div>
                <div className="flex items-center space-x-2 text-red-500">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-xs sm:text-sm font-medium">Gastos</span>
                </div>
              </div>
            </div>
            <div className="h-64 sm:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={historicalData}>
                  <defs>
                    <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#5fc500" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#5fc500" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="outcomeGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="month"
                    stroke="#64748b"
                    fontSize={12}
                    fontWeight={500}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    stroke="#64748b"
                    fontSize={12}
                    fontWeight={500}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    formatter={(value, name) => [
                      `$${value.toLocaleString("es-MX", { minimumFractionDigits: 2 })}`,
                      name === 'income' ? 'Ingresos' : 'Gastos'
                    ]}
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      boxShadow: '0 10px 25px -5px rgb(0 0 0 / 0.1)',
                      fontSize: '12px',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="income"
                    stroke="#5fc500"
                    strokeWidth={3}
                    fill="url(#incomeGradient)"
                  />
                  <Area
                    type="monotone"
                    dataKey="outcome"
                    stroke="#ef4444"
                    strokeWidth={3}
                    fill="url(#outcomeGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Category Spending */}
          <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-lg border border-slate-200/50">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900">Gastos por Categoría</h3>
                <p className="text-sm text-slate-600">Este período</p>
              </div>
              <button
                onClick={() => navigate("/expenses")}
                className="text-[#5fc500] hover:text-[#218e01] font-semibold text-xs sm:text-sm flex items-center gap-1"
              >
                Ver todas
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {expensesByCategory.length > 0 ? (
              <>
                <div className="h-48 sm:h-64 mb-6">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={expensesByCategory}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={8}
                        dataKey="value"
                      >
                        {expensesByCategory.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [`$${value.toLocaleString("es-MX", { minimumFractionDigits: 2 })}`, 'Gasto']}
                        contentStyle={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '12px',
                          boxShadow: '0 10px 25px -5px rgb(0 0 0 / 0.1)',
                          fontSize: '12px',
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2 sm:space-y-3">
                  {expensesByCategory.slice(0, 5).map((category, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 sm:p-3 hover:bg-slate-50 rounded-xl transition-colors cursor-pointer"
                      onClick={() => navigate(`/expenses?category=${category.name}`)}
                    >
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-3 h-3 rounded-full shadow-sm"
                          style={{ backgroundColor: category.color }}
                        />
                        <span className="text-xs sm:text-sm font-medium text-slate-900">
                          {category.name}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="text-xs sm:text-sm font-semibold text-slate-900">
                          ${category.value.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                        </p>
                        <p className="text-xs text-slate-500">
                          {totalPeriodExpenses > 0
                            ? `${((category.value / totalPeriodExpenses) * 100).toFixed(0)}%`
                            : "0%"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <Activity className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h4 className="text-sm font-medium text-slate-900 mb-2">Sin actividad</h4>
                <p className="text-xs text-slate-600 mb-4">No hay gastos registrados este período</p>
                <button
                  onClick={() => navigate("/expenses/new")}
                  className="text-[#5fc500] hover:text-[#218e01] font-semibold text-sm flex items-center gap-1 mx-auto"
                >
                  <Plus className="w-4 h-4" />
                  Registrar primer gasto
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Transaction History & Upcoming Subscriptions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Transaction History */}
          <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-lg border border-slate-200/50">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900">Transacciones Recientes</h3>
                <p className="text-sm text-slate-600">Últimos movimientos</p>
              </div>
              <button
                onClick={() => navigate('/expenses')}
                className="flex items-center space-x-2 text-[#5fc500] hover:text-[#218e01] font-semibold text-xs sm:text-sm transition-colors"
              >
                <span>Ver todas</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-2 sm:space-y-3">
              {recentTransactions.length > 0 ? (
                recentTransactions.map((transaction) => {
                  const Icon = transaction.icon;
                  return (
                    <div
                      key={transaction.id}
                      className="flex items-center justify-between p-3 sm:p-4 hover:bg-slate-50 rounded-xl transition-all duration-200 border border-slate-100 cursor-pointer"
                      onClick={() => navigate(transaction.type === "expense" ? `/expenses` : `/incomes`)}
                    >
                      <div className="flex items-center space-x-3 sm:space-x-4">
                        <div className={`p-2 sm:p-3 ${transaction.bgColor} rounded-xl shadow-sm`}>
                          <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${transaction.color}`} />
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm font-semibold text-slate-900 mb-1">
                            {transaction.title}
                          </p>
                          <p className="text-xs text-slate-500">
                            {transaction.category} • {transaction.date}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-xs sm:text-sm font-semibold ${transaction.color}`}>
                          {transaction.amount}
                        </p>
                        <div className="flex items-center justify-end space-x-1 mt-1">
                          {transaction.status === "completed" ? (
                            <CheckCircle className="w-3 h-3 text-[#5fc500]" />
                          ) : (
                            <Clock className="w-3 h-3 text-[#218e01]" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-12">
                  <Receipt className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <h4 className="text-sm font-medium text-slate-900 mb-2">
                    No hay transacciones recientes
                  </h4>
                  <p className="text-xs text-slate-600">
                    Tus movimientos aparecerán aquí
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Upcoming Subscriptions */}
          <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-lg border border-slate-200/50">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900">Pagos Próximos</h3>
                <p className="text-sm text-slate-600">
                  {upcomingSubscriptions.length} en los próximos 7 días
                </p>
              </div>
              <button
                onClick={() => navigate('/subscriptions')}
                className="text-[#5fc500] hover:text-[#218e01] font-semibold text-xs sm:text-sm flex items-center gap-1"
              >
                Ver todas
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {loadingSubscriptions ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse flex items-center space-x-4 p-3">
                    <div className="w-12 h-12 bg-slate-200 rounded-xl" />
                    <div className="flex-1">
                      <div className="h-4 bg-slate-200 rounded mb-2" />
                      <div className="h-3 bg-slate-100 rounded w-2/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : upcomingSubscriptions.length > 0 ? (
              <div className="space-y-2 sm:space-y-3">
                {upcomingSubscriptions.map((sub) => {
                  const daysUntil = Math.ceil((new Date(sub.fechaProximoPago) - new Date()) / (1000 * 60 * 60 * 24));
                  return (
                    <div
                      key={sub.id}
                      className="flex items-center justify-between p-3 sm:p-4 hover:bg-orange-50 rounded-xl transition-colors border border-orange-100 cursor-pointer"
                      onClick={() => navigate("/subscriptions")}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-orange-400 to-orange-500 rounded-xl flex items-center justify-center shadow-sm">
                          <Bell className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm font-semibold text-slate-900">
                            {sub.nombre}
                          </p>
                          <p className="text-xs text-slate-600">
                            {daysUntil === 0 ? "Hoy" : `En ${daysUntil} día${daysUntil > 1 ? "s" : ""}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-xs sm:text-sm font-semibold text-slate-900">
                          ${sub.cantidad.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                        </span>
                        <p className="text-xs text-slate-500 capitalize">
                          {sub.frecuenciaPago}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <CreditCard className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h4 className="text-sm font-medium text-slate-900 mb-2">
                  No hay pagos próximos
                </h4>
                <p className="text-xs text-slate-600">
                  Tus suscripciones con pagos próximos aparecerán aquí
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-slate-900">Acciones Rápidas</h3>
              <p className="text-sm text-slate-600">Accede rápidamente a tus herramientas</p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <button
                  key={index}
                  onClick={() => navigate(item.path)}
                  className="group bg-white rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-2xl border border-slate-200/50 transition-all duration-300 text-center hover:-translate-y-1"
                >
                  <div className={`w-10 h-10 sm:w-12 sm:h-14 bg-gradient-to-r ${item.color} rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-5 h-5 sm:w-6 sm:h-7 text-white" />
                  </div>
                  <h4 className="font-bold text-slate-900 text-xs sm:text-sm mb-1">
                    {item.title}
                  </h4>
                  {item.badge && (
                    <span className="inline-block bg-gradient-to-r from-[#218e01] to-[#5fc500] text-white text-[10px] px-2 py-0.5 rounded-full font-semibold shadow-sm">
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Monthly Comparison & Insights */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <div className="bg-gradient-to-r from-[#096365] to-[#5ba49e] rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-white shadow-lg">
            <div className="flex items-center space-x-4 mb-4">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-lg">Análisis del Período</h4>
                <p className="text-white/70 text-sm">Comparación con período anterior</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-white/90">Ingresos</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold">
                    ${comparison.currentIncomes.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                  </span>
                  {comparison.incomeChange >= 0 ? (
                    <ArrowUpRight className="w-4 h-4 text-green-300" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4 text-red-300" />
                  )}
                  <span className={`text-sm ${comparison.incomeChange >= 0 ? "text-green-300" : "text-red-300"}`}>
                    {comparison.incomeChange >= 0 ? "+" : ""}{comparison.incomeChange}%
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/90">Gastos</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold">
                    ${comparison.currentExpenses.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                  </span>
                  {comparison.expenseChange >= 0 ? (
                    <ArrowUpRight className="w-4 h-4 text-red-300" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4 text-green-300" />
                  )}
                  <span className={`text-sm ${comparison.expenseChange >= 0 ? "text-red-300" : "text-green-300"}`}>
                    {comparison.expenseChange >= 0 ? "+" : ""}{comparison.expenseChange}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-white shadow-lg">
            <div className="flex items-center space-x-4 mb-4">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <PiggyBank className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-lg">Recomendación</h4>
                <p className="text-white/70 text-sm">Optimiza tus finanzas</p>
              </div>
            </div>
            {totalMonthlySubscriptions > 0 && totalPeriodIncomes > 0 ? (
              <div>
                <p className="text-white/90 mb-4">
                  Tus suscripciones representan el{" "}
                  <span className="font-bold">
                    {((totalMonthlySubscriptions / totalPeriodIncomes) * 100).toFixed(1)}%
                  </span>{" "}
                  de tus ingresos este período.
                  {totalMonthlySubscriptions / totalPeriodIncomes > 0.3 && (
                    <span className="block mt-2">
                      Considera revisar tus suscripciones para optimizar tus gastos.
                    </span>
                  )}
                </p>
                <button
                  onClick={() => navigate("/subscriptions")}
                  className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl text-sm font-semibold backdrop-blur-sm transition-colors"
                >
                  Revisar suscripciones
                </button>
              </div>
            ) : (
              <div>
                <p className="text-white/90 mb-4">
                  Establece metas de ahorro y sigue tu progreso para alcanzar tus objetivos financieros.
                </p>
                <button
                  onClick={() => navigate("/dashboard")}
                  className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl text-sm font-semibold backdrop-blur-sm transition-colors"
                >
                  Ver más detalles
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default HomeDashboard;
